import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, QrCode } from 'lucide-react';
import QRGenerator from './QRGenerator';
import { backendUrl } from '../../utils/socketClient';

interface SurveySetupProps {
  shopName: string;
  userEmail: string;
}

interface SurveyConfig {
  enabled: boolean;
  welcomeMessage: string;
  thanksMessage: string;
  emailRequired: boolean;
  ageGroupRequired: boolean;
  visitFrequencyRequired: boolean;
  genderRequired: boolean;
}

const SurveySetup: React.FC<SurveySetupProps> = ({ shopName, userEmail }) => {
  const [config, setConfig] = useState<SurveyConfig>({
    enabled: true,
    welcomeMessage: 'ご来店ありがとうございます。簡単なアンケートにお答えいただくと、口コミの下書きを自動生成いたします。',
    thanksMessage: 'アンケートにご協力いただき、ありがとうございました。',
    emailRequired: false,
    ageGroupRequired: false,
    visitFrequencyRequired: false,
    genderRequired: true
  });

  const [activeTab, setActiveTab] = useState<'settings' | 'qr'>('settings');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  // 設定を読み込み
  useEffect(() => {
    loadSettings();
  }, [shopName, userEmail]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/survey-settings/${encodeURIComponent(shopName)}?user_email=${encodeURIComponent(userEmail)}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // デフォルト値でマージして設定
          setConfig({
            enabled: result.settings.enabled ?? true,
            welcomeMessage: result.settings.welcome_message ?? 'ご来店ありがとうございます。簡単なアンケートにお答えいただくと、口コミの下書きを自動生成いたします。',
            thanksMessage: result.settings.thanks_message ?? 'アンケートにご協力いただき、ありがとうございました。',
            emailRequired: result.settings.email_required ?? false,
            ageGroupRequired: result.settings.age_group_required ?? false,
            visitFrequencyRequired: result.settings.visit_frequency_required ?? false,
            genderRequired: result.settings.gender_required ?? true
          });
        }
      } else {
        console.error('設定読み込みエラー:', response.status);
      }
    } catch (error) {
      console.error('設定読み込み中にエラーが発生しました:', error);
    } finally {
      setLoading(false);
    }
  };

  // 設定を保存
  const saveConfig = async () => {
    setSaving(true);
    try {
      // APIに送信するデータ形式に変換
      const saveData = {
        enabled: config.enabled,
        welcome_message: config.welcomeMessage,
        thanks_message: config.thanksMessage,
        email_required: config.emailRequired,
        age_group_required: config.ageGroupRequired,
        visit_frequency_required: config.visitFrequencyRequired,
        gender_required: config.genderRequired
      };

      const response = await fetch(`${backendUrl}/survey-settings/${encodeURIComponent(shopName)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail
        },
        body: JSON.stringify(saveData)
      });

      const result = await response.json();
      if (result.success) {
        setLastSaved(new Date());
        alert('設定を保存しました');
      } else {
        alert('保存に失敗しました: ' + result.error);
      }
    } catch (error) {
      console.error('保存エラー:', error);
      alert('保存中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* タブナビゲーション */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            アンケート設定
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'qr'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <QrCode className="h-4 w-4 inline mr-1" />
            QRコード生成
          </button>
        </nav>
      </div>

      <div className="p-6">
        {/* ローディング表示 */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">設定を読み込み中...</span>
          </div>
        )}

        {/* アンケート設定タブ */}
        {!loading && activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">
                アンケート設定
              </h2>
              <div className="flex items-center space-x-3">
                {lastSaved && (
                  <span className="text-sm text-gray-500">
                    最終保存: {lastSaved.toLocaleTimeString('ja-JP')}
                  </span>
                )}
                <button
                  onClick={saveConfig}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {saving ? '保存中...' : '設定を保存'}
                </button>
              </div>
            </div>

            {/* 基本設定 */}
            <div className="space-y-4">
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                    className="rounded"
                  />
                  <span className="font-medium text-gray-700">アンケート機能を有効にする</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ウェルカムメッセージ
                </label>
                <textarea
                  value={config.welcomeMessage}
                  onChange={(e) => setConfig({...config, welcomeMessage: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="お客様への挨拶メッセージ"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  完了メッセージ
                </label>
                <textarea
                  value={config.thanksMessage}
                  onChange={(e) => setConfig({...config, thanksMessage: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="アンケート完了後のメッセージ"
                />
              </div>
            </div>

            {/* 表示項目設定 */}
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-3">表示項目設定</h3>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config.emailRequired}
                    onChange={(e) => setConfig({...config, emailRequired: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-gray-700">メールアドレスを必須にする</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config.ageGroupRequired}
                    onChange={(e) => setConfig({...config, ageGroupRequired: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-gray-700">年齢層を必須にする</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config.visitFrequencyRequired}
                    onChange={(e) => setConfig({...config, visitFrequencyRequired: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-gray-700">来店頻度を必須にする</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={config.genderRequired}
                    onChange={(e) => setConfig({...config, genderRequired: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-gray-700">性別を必須にする</span>
                </label>
              </div>
            </div>

          </div>
        )}

        {/* QRコード生成タブ */}
        {!loading && activeTab === 'qr' && (
          <QRGenerator shopName={shopName} userEmail={userEmail} />
        )}

      </div>
    </div>
  );
};

export default SurveySetup;